import dayjs from 'dayjs'

/**
 * Formats a game date into uppercase format
 * Example output: "JAN 1 - 11:59 AM"
 * @param gameDate - dayjs date object to format
 * @returns Formatted date string in uppercase
 */
export const formatGameDate = (gameDate: dayjs.Dayjs): string => {
  const formattedDate: string = gameDate.format('MMM D - h:mm A').toUpperCase()
  return formattedDate
}

/**
 * Formats a date string to display in a long format with ordinal day
 * Example output: "January 1, 2024 11:59 AM"
 * @param dateString - ISO date string to format
 * @returns Formatted date string
 */
export const formatLongDateTime = (dateString: string): string => {
  return dayjs(dateString).utc().format('MMMM D, YYYY h:mm A')
}

/**
 * Formats a date string to display in a short format with time
 * Example output: "Jan 1, 2024 11:59 AM"
 * @param dateString - ISO date string to format
 * @param useUtc - Whether to use UTC timezone (default: true)
 * @param timezone - Optional timezone to convert to (e.g. 'America/New_York')
 * @returns Formatted date string
 */
export const formatShortDateTime = (
  dateString: string,
  useUtc: boolean = true,
  timezone?: string
): string => {
  let date = useUtc ? dayjs(dateString).utc() : dayjs(dateString)
  if (timezone) {
    date = date.tz(timezone)
  }
  return date.format('MMM D, YYYY h:mm A')
}

/**
 * Formats a date string to display in a long format with ordinal day
 * Example output: "January 1, 2024"
 * @param dateString - ISO date string to format
 * @returns Formatted date string
 */
export const formatLongDate = (dateString: string): string => {
  return dayjs(dateString).utc().format('MMMM D, YYYY')
}

/**
 * Formats a date string to display in a short format
 * Example output: "Jan 1, 2024"
 * @param dateString - ISO date string to format
 * @returns Formatted date string
 */
export const formatShortDate = (
  dateString: string,
  useUtc: boolean = true
): string => {
  const date = useUtc ? dayjs(dateString).utc() : dayjs(dateString)
  return date.format('MMM D, YYYY')
}

/**
 * Returns a date string in ISO format with the time set to 23:58:58 UTC,
 * This is used to indicate that the game time is to be defined.
 * @param isoDateString - ISO date string (e.g., from locationStartDate.toUTC().toISO())
 * @returns ISO date string with time set to 23:58:58
 */
export const setTimeToBeDefined = (isoDateString: string): string => {
  if (typeof isoDateString !== 'string' || isoDateString.length === 0) {
    return isoDateString
  }
  return dayjs(isoDateString)
    .utc()
    .set('hour', 23)
    .set('minute', 58)
    .set('second', 58)
    .set('millisecond', 0)
    .toISOString()
}

/**
 * Returns a date string in ISO format with the time set to 23:58:58 UTC,
 * This is used to indicate that the game time is to be defined.
 * @param isoDateString - ISO date string (e.g., from locationStartDate.toUTC().toISO())
 * @returns ISO date string with time set to 23:58:58
 */
export const setTimeToBeDefinedNoISO = (isoDateString: string): string => {
  if (typeof isoDateString !== 'string' || isoDateString.length === 0) {
    return isoDateString
  }
  return dayjs(isoDateString)
    .utc()
    .set('hour', 23)
    .set('minute', 58)
    .set('second', 58)
    .set('millisecond', 0)
    .toString()
}

/**
 * Checks if the given ISO date string has the time set to 23:58:58 UTC,
 * which indicates that the game time is to be defined.
 * @param isoDateString - ISO date string to check
 * @returns True if the time is set to 23:58:58 UTC, false otherwise
 */
export const isTimeToBeDefined = (isoDateString: string): boolean => {
  if (typeof isoDateString !== 'string' || isoDateString.length === 0) {
    return false
  }
  const date = dayjs(isoDateString).utc()
  return (
    date.hour() === 23 &&
    date.minute() === 58 &&
    date.second() === 58 &&
    date.millisecond() === 0
  )
}
